<template>
  <EUICard bordered flat border-radius="8px" class="flex q-pa-lg">
    <ApprovalRejectedMessage
      v-if="approvalStatus === $Constants.HR_APPROVAL_STATUSES.denied.value"
      :name="this.approval.hrApprovedByName"
      :reason="
        $Constants.HR_APPROVAL_REASONS[this.approval.approvalReason].label
      "
      :notes="this.approval.approvalNotes"
    />
    <div class="row q-col-gutter-lg">
      <div class="col-xs-12 col-md-6">
        <EUICard border-radius="4px" class="q-pa-lg bdr-a-grey-3 q-mb-lg">
          <HRApprovalEmployeeDetails
            :name="applicantName"
            :address="computedAddress | AddressFilter"
            :payroll-number="approval.payrollNumber"
            :cost-center="approval.costCenter"
            :payroll-cost-center-editable="payrollCostCenterEditable"
            :editing="editingEmployeeDetails"
            :saving="updating"
            @update:payroll-number="
              $emit('update:employee-details', { payrollNumber: $event })
            "
            @update:cost-center="
              $emit('update:employee-details', { costCenter: $event })
            "
            @save="$emit('save:approval-updates')"
            @edit="$emit('editing:employee-details')"
            @cancel="$emit('cancel:employee-details')"
          />
        </EUICard>
        <EUICard border-radius="4px" class="q-pa-lg bdr-a-grey-3">
          <HRApprovalQuoteDetails
            :fbt-value="quote.fbtValue"
            :monthly-rental="quote.monthlyRentalNet"
            :income="creditCheck.income"
            :income-type="incomeType"
            :income-period="creditCheck.incomePeriod"
            :title="quote.title"
          />
        </EUICard>
      </div>
      <div class="col-xs-12 col-md-6">
        <HRApprovalTerms :employer-name="employerName" />
        <div class="row q-gutter-sm q-mt-sm print-hide">
          <DocumentBtn
            v-for="document in documents"
            :key="document.href"
            :href="document.href"
            :label="document.label"
          />
        </div>
        <div class="q-mt-lg q-pt-sm">
          <p v-if="isNovatedContract">
            Please click below to open and digitally sign the Novated Settlement
            document for this employee
          </p>
          <p v-else>
            Please click below to open to view the Novated Quote for this
            employee
          </p>
          <SAIBtn
            v-if="isNovatedContract && metaData.fileId"
            class="q-mt-sm rounded-borders"
            outline
            padding="sm 12px"
            no-caps
            :rounded="false"
            border-width="1px"
            color="grey-4"
            :loading="loadingContractPdf"
            @click="$emit('download:contract')"
          >
            <QIcon
              name="eva-file-text-outline"
              color="primary"
              size="xs"
              class="q-mr-sm"
            />
            <span class="text-primary text-weight-medium">
              Contract #{{ approval.referenceNumber }}/{{
                approval.referenceVersion
              }}
            </span>
          </SAIBtn>
          <SAIBtn
            v-else-if="!isNovatedContract"
            class="q-mt-sm rounded-borders"
            outline
            padding="sm 12px"
            no-caps
            :rounded="false"
            border-width="1px"
            color="grey-4"
            :loading="loadingQuotePdf"
            @click="$emit('download:quote')"
          >
            <QIcon
              name="eva-file-text-outline"
              color="primary"
              size="xs"
              class="q-mr-sm"
            />
            <span class="text-primary text-weight-medium">
              Quote #{{ approval.referenceNumber }}/{{
                approval.referenceVersion
              }}
            </span>
          </SAIBtn>
        </div>
        <div
          class="row q-mt-md print-hide"
          v-if="
            approvalStatus === $Constants.HR_APPROVAL_STATUSES.pending.value
          "
        >
          <SAIBtn
            v-if="isNovatedContract"
            size="16px"
            no-caps
            padding="10px lg"
            label="Open Agreement"
            color="primary"
            :loading="openingAgreement"
            :disable="saving || disable"
            @click="$emit('open-agreement')"
          />
          <SAIBtn
            v-if="!isNovatedContract"
            size="16px"
            no-caps
            padding="10px lg"
            label="Approve"
            icon="eva-checkmark-outline"
            color="primary"
            :loading="approving"
            :disable="saving || disable"
            @click="$emit('approve')"
          />
          <SAIBtn
            v-if="!isNovatedContract"
            size="16px"
            no-caps
            padding="10px lg"
            label="Deny"
            class="q-ml-sm"
            icon="eva-close-outline"
            color="negative"
            :disable="saving || disable"
            @click="$emit('open:deny-reason')"
          />
          <SAIBtn
            v-if="!isNovatedContract"
            size="16px"
            no-caps
            color="grey-8"
            label="Do it later"
            flat
            class="q-ml-sm"
            padding="sm"
            dense
            :disable="saving || disable"
            @click="$emit('decide-later')"
          />
        </div>
        <div class="q-mt-md" v-else>
          <h6 class="text-capitalize">{{ approvalStatus }}</h6>
          <HRCardLabel>
            {{ isNovatedContract ? 'CONTRACT STATUS' : 'QUOTE STATUS' }}
          </HRCardLabel>
        </div>
        <RejectApprovalDialog
          :show="hrApprovalDenyOpen"
          :reason="approval.approvalReason"
          :notes="approval.approvalNotes"
          :saving="denying"
          :disable="saving || disable"
          @update:reason="$emit('update:reason', $event)"
          @update:notes="$emit('update:notes', $event)"
          @cancel="$emit('close:deny-reason')"
          @confirm="$emit('deny')"
        />
      </div>
    </div>
  </EUICard>
</template>

<script>
import { QIcon } from 'quasar';
import AddressFilter from '@eui-filter/address';
import { SAIBtn } from 'ecx.saigon.ui.common';
import EUICard from '@eui-atom/card';

export default {
  name: 'HRApprovalCard',
  props: {
    applicantName: String,
    employerName: String,
    openingAgreement: Boolean,
    approving: Boolean,
    denying: Boolean,
    hrApprovalDenyOpen: Boolean,
    loadingQuotePdf: Boolean,
    loadingContractPdf: Boolean,
    editingEmployeeDetails: Boolean,
    approval: {
      type: Object,
      default: () => ({})
    },
    documents: {
      type: Array,
      default: () => []
    },
    saving: Boolean,
    disable: Boolean,
    updating: Boolean,
    payrollCostCenterEditable: Boolean,
    approvalStatus: String,
    application: {
      type: Object,
      default: () => ({})
    },
    isNovatedContract: Boolean
  },
  computed: {
    metaData() {
      return this.application.metaData || {};
    },
    creditCheck() {
      return this.metaData.creditCheck || {};
    },
    quote() {
      return this.metaData.acceptedQuote || {};
    },
    computedAddress() {
      const address = this.creditCheck.address || {};
      return {
        ...address,
        address: address.address2,
        address2: address.address
      };
    },
    incomeType() {
      return this.creditCheck.incomePeriod === 'beforeTax' ? 'Gross' : 'Net';
    }
  },
  filters: {
    AddressFilter
  },
  components: {
    EUICard,
    SAIBtn,
    QIcon,
    HRApprovalEmployeeDetails: () => import('../hr-approval-employee-details'),
    HRApprovalQuoteDetails: () => import('../hr-approval-quote-details'),
    HRApprovalTerms: () => import('../hr-approval-terms'),
    HRCardLabel: () => import('../../atom/hr-card-label'),
    RejectApprovalDialog: () => import('../../molecule/reject-approval-dialog'),
    ApprovalRejectedMessage: () =>
      import('../../molecule/approval-rejected-message'),
    DocumentBtn: () => import('../../atom/document-btn')
  }
};
</script>

<style lang="sass" scoped>
.hr-approval-card
  box-shadow: 0 2px 6px 0 rgba(0,0,0,0.05)
</style>
